import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Typography, Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import {
    getUserMapCreditData, updateUserMapCredits,
    addOwnGoogleKey, adhocCredit,
    mapUserCreditSelector
} from '../../features/user/UserSlice';
import { Button } from '@mui/material';


const useStyles = makeStyles((theme) => ({
    root: {
        '& > *': {
            margin: useTheme().spacing(1),
            width: '25ch',
        },
    },
    groot: {
        '& > *': {
            margin: useTheme().spacing(1),
            width: '25ch',
        },
    },
}));


const GoogleCreditLimit = ({ googleKeyOpen, handleGoogleKeyClose, mapUser }) => {

    const classes = useStyles();
    const dispatch = useDispatch();
    const userMapCreditData = useSelector(mapUserCreditSelector);

    const [creditData, setCreditData] = useState({});
    const [ownKey, setOwnKey] = useState("");
    const [keySource, setKeySource] = useState(null);
    const [dailyLimit, setDailyLimit] = useState(5);
    const [monthlyLimit, setMonthlyLimit] = useState(150);
    const [balanceQty, setBalanceQty] = useState(1);

    useEffect(() => {
        if (mapUser === null) {
            setCreditData({});
        } else {
            dispatch(getUserMapCreditData({ mapUser: mapUser.userid }));
        }
    }, [dispatch, mapUser]);

    useEffect(() => {
        if (userMapCreditData) {
            //console.log("Credit data updated", userMapCreditData);
            setCreditData(userMapCreditData);
            setKeySource(userMapCreditData.keySource);
            setDailyLimit(userMapCreditData.dailyLimit);
            setMonthlyLimit(userMapCreditData.monthlyLimit);
        }
    }, [userMapCreditData]);



    const onDailyLimitChange = (e) => {
        setDailyLimit(e.target.value);
        setMonthlyLimit(30 * e.target.value);
    }

    const giveAdHocBalance = (e) => {
        e.preventDefault();
        dispatch(adhocCredit({ mapUser: mapUser.userid, balanceQty }))
        dispatch(getUserMapCreditData({ mapUser: mapUser.userid }));
        handleGoogleKeyClose();
    }

    const creditUpdate = (e) => {
        e.preventDefault();
        dispatch(updateUserMapCredits({ dailyLimit, monthlyLimit, mapUser: mapUser.userid, keySource }));
        dispatch(getUserMapCreditData({ mapUser: mapUser.userid }));
        handleGoogleKeyClose();
    }

    const addOwnKey = (e) => {
        e.preventDefault();
        dispatch(addOwnGoogleKey({ ownKey, mapUser: mapUser.userid }));
        setOwnKey("");
        dispatch(getUserMapCreditData({ mapUser: mapUser.userid }));
        handleGoogleKeyClose();
    }

    if (mapUser !== null) {
        return (
            <Dialog open={googleKeyOpen} onClose={handleGoogleKeyClose}>
                <div style={{ width: '600px' }}>
                    <DialogTitle><b>Google Map Credit Control</b></DialogTitle>
                    <DialogContent>
                        {
                            creditData.inactive ?
                                <DialogContentText>
                                    <Typography varinat="body1" color="secondary"><b>{mapUser.username}</b> is Inactive</Typography>
                                </DialogContentText>
                                :
                                <Grid container spacing={3}>
                                    <Grid item sm={6}>
                                        <DialogContentText>
                                            <Typography varinat="body1" color="primary"><b>User :</b>{` ${mapUser.username} (${mapUser.userid})`}</Typography>
                                            <Typography varinat="body1"><b>Key :</b>{` ${creditData.ownKey === 1 ? "Owned" : "Shared"}`}</Typography>
                                            {creditData.ownKey === 0 && <Typography varinat="body1"><b>Key Source :</b>{` ${creditData.keySource}`}</Typography>}
                                        </DialogContentText>
                                        <DialogContentText>
                                            <Typography variant="body1" color="primary"><b>Today</b></Typography>
                                            {creditData.ownKey === 0 && <Typography varinat="body2"><b>Limit :</b>{` ${creditData.dailyLimit}`}</Typography>}
                                            <Typography varinat="body2"><b>Consumption :</b>{` ${creditData.todayConsumption ? creditData.todayConsumption : 0}`}</Typography>
                                        </DialogContentText>
                                        <DialogContentText>
                                            <Typography variant="body1" color="primary"><b>This Month</b></Typography>
                                            {creditData.ownKey === 0 && <Typography varinat="body2"><b>Limit :</b>{` ${creditData.monthlyLimit}`}</Typography>}
                                            <Typography varinat="body2"><b>Consumption :</b>{` ${creditData.thisMonthConsumption ? creditData.thisMonthConsumption : 0}`}</Typography>
                                        </DialogContentText>
                                    </Grid>
                                    <Grid item sm={6}>
                                        <DialogContentText>
                                            <Typography variant="body1" color="primary"><b>Recent Usage</b></Typography>
                                            {
                                                creditData.latestTransactions && creditData.latestTransactions.length > 0 &&
                                                creditData.latestTransactions.map(tr =>
                                                    <Typography varinat="subtitle2">{`${moment(tr.transaction_time).format('D/M/YYYY h:mm a')}`}</Typography>
                                                )
                                            }
                                        </DialogContentText>
                                    </Grid>
                                </Grid>
                        }

                        <Grid container spacing={3}>
                            <Grid item sm={12}>
                                <Typography variant="body1" color="primary"><b>{creditData.inactive ? "Activate" : "Update"} Google Map Usage Limits & Key Source</b></Typography>
                                <form className={classes.root} noValidate autoComplete="off" onSubmit={(e) => creditUpdate(e)}>
                                    <TextField variant="standard" label="Key Source Userid" type="number" onChange={e => setKeySource(e.target.value)} value={keySource} />
                                    <TextField variant="standard" label="Daily Limit" type="number" onChange={onDailyLimitChange} value={dailyLimit} />
                                    <TextField variant="standard" label="Monthly Limit" type="number" onChange={e => setKeySource(e.target.value)} value={monthlyLimit} />
                                    <Button type="submit" variant="contained" color="primary" size="small">{creditData.inactive ? "Activate" : "Update"}</Button>
                                </form>
                            </Grid>
                            <Grid item sm={6}>
                                <Typography variant="body1" color="primary"><b>Add User's Own Google Map Key</b></Typography>
                                {creditData.ownKey === 1 && <Typography variant="caption" color="secondary"><b>User has already added one or more keys. </b></Typography>}
                                <form className={classes.groot} noValidate autoComplete="off" onSubmit={(e) => addOwnKey(e)}>
                                    <TextField variant="standard" label="Key String" type="text" onChange={e => setOwnKey(e.target.value)} value={ownKey} />
                                    <Button type="submit" variant="contained" color="primary" size="small">{creditData.ownKey === 1 ? "Add Additional Key" : "Add Key"}</Button>
                                </form>
                            </Grid>
                            {
                                /*(creditData.todayConsumption >= creditData.dailyLimit || creditData.thisMonthConsumption >= creditData.monthlyLimit) &&*/ creditData.ownKey === 0 &&
                                <Grid item sm={6}>
                                    <Typography variant="body1" color="secondary"><b>Give Complimentary Credit</b></Typography>

                                    <form className={classes.groot} noValidate autoComplete="off" onSubmit={(e) => giveAdHocBalance(e)}>
                                        <TextField variant="standard" label="Credit Quantity  (Max 10)" type="number" onChange={e => setBalanceQty(e.target.value)} value={balanceQty} />
                                        <Button type="submit" variant="contained" color="secondary" size="small">Add Credit</Button>
                                    </form>
                                </Grid>

                            }

                        </Grid>

                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleGoogleKeyClose} color="secondary" variant="contained">
                            Close
                        </Button>
                    </DialogActions>
                </div>
            </Dialog>
        );
    } else {
        return null;
    }

}

export default GoogleCreditLimit;